// @flow
import React, { useCallback, useState, useEffect } from 'react';
import { Container, Button } from 'semantic-ui-react';
import { errorLogger, infoLogger } from '../../helpers';
import Header from 'views/Header';
import * as css from './SmsChallenge.module.css';
import api from 'api';
import Footer from 'components/Footer';
import { useSelector } from 'react-redux';
import Loading from 'components';

type SmsChallengeProps = {
  setLoggedInStatus: (value: Boolean) => void,
  setUserAuthData: () => void,
  eid: String,
  iccid: String,
  isTransfer: boolean,
};

const SmsChallenge = ({ setLoggedInStatus, setUserAuthData, eid, iccid, isTransfer }: SmsChallengeProps) => {
  const [validationCode, setValidationCode] = useState('');
  const [isValidationCodeSent, setIsValidationCodeSent] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [isButtonDisabled, setButtonDisabled] = useState(false);

  const [validationAtemptCountDown, setValidationCountDown] = useState(3);

  const [errorMessage, setErrorMessage] = useState('');

  const msisdn = useSelector((state) => state.app.msisdn);

  const validationInputCallback = useCallback(
    (smsCode) => {
      setValidationCode(smsCode);
      setButtonDisabled(smsCode.length !== 6);
    },
    [setButtonDisabled, setValidationCode, validationCode]
  );

  const sendSmsCode = useCallback(async () => {
    try {
      setIsLoading(true);
      setButtonDisabled(true);
      setErrorMessage('');
      await api.sendSmsCode({
        msisdn: msisdn,
      });
      setIsLoading(false);
      setIsValidationCodeSent(true);
    } catch (error) {
      setIsLoading(false);
      setButtonDisabled(false);
      setErrorMessage(
        'Ett fel uppstod när vi försökte skicka din valideringskod. Vänligen försök igen.\\n Om felet fortsätter, var vänlig kontakta kundtjänst på 0200-23 23 23'
      );
      errorLogger({
        name: 'client-send-sms-challenge',
        log: `sendSmsChallenge failed: ${error.message}`,
      });
    }
  }, []);

  const startValidation = useCallback(async () => {
    try {
      setIsLoading(true);
      setButtonDisabled(true);
      setErrorMessage('');

      const data = window.__POST_DATA__;
      let jsonWebToken;
      if (data !== undefined) {
        const newData = window.atob(data);
        jsonWebToken = JSON.parse(newData).carrierPostData;
      }
      const loginPostData = {
        code: validationCode,
        msisdn,
        eid,
        iccid,
        postData: data,
        token: jsonWebToken,
        brand: window.__VENDOR__,
      };
      const result = await api.sendValidateSmsChallange({ loginData: loginPostData, isTransfer });

      if (result.status === 200) {
        infoLogger({ name: 'client-sms-validation', log: 'Sms code successfully validated' });
        const subscriptionResult = result.data.data;
        if (subscriptionResult) {
          setUserAuthData(subscriptionResult);
          setLoggedInStatus(true);
          setIsLoading(true);
        }
      }
    } catch (error) {
      errorLogger({
        name: 'client-sms-validation',
        log: `Sms validation failed, code: ${validationCode}. Error: ${error.message}`,
      });
      setButtonDisabled(false);
      setIsLoading(false);
      if (validationAtemptCountDown > 0) {
        setErrorMessage('Valideringen misslyckades. Var vänlig kontrollera att du angivit rätt kod och försök igen.');
      } else {
        infoLogger({
          name: 'client-sms-validation',
          log: 'Sms validation reset, user maxed out number of tries for one challange.',
        });

        setErrorMessage(
          'Du har överskridit antalet försök att validera koden. Var vänlig begär en ny kod och försök igen. Om problemet fortsätter, kontakta kundtjänst.'
        );
        setIsValidationCodeSent(false);
        setValidationCountDown(3);
      }

      setValidationCountDown(validationAtemptCountDown - 1);
    }
  }, [validationAtemptCountDown, validationCode]);

  const formatText = (text) => {
    return text
      ? text.split('\\n').map((item, i) => {
          return (
            <p key={i} className={css.error}>
              {item}
            </p>
          );
        })
      : '';
  };

  useEffect(() => {
    window.__PAGE__ = 'Login-sms';
    infoLogger({ name: 'client-page-view-sms-login', log: 'Sms login page shown' });
  }, []);

  if (typeof msisdn === 'undefined') return <Loading />;

  return (
    <div>
      <Header />
      <Container>
        <h1>Verifiera nummer</h1>
        <p>Du behöver validera att du äger numret för att gå vidare.</p>
        {isValidationCodeSent && (
          <div className={css.input}>
            <input
              type="text"
              name="validation"
              autoCapitalize="off"
              autoComplete="one-time-code"
              onChange={(event) => validationInputCallback(event.target.value)}
            />
          </div>
        )}
        {isLoading && (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" className={css.spinner}>
            <path
              fill="#3A3B3F"
              d="M10 40v-1.1-1.3-.8c0-.3.1-.6.1-.9.1-.6.1-1.4.2-2.1.2-.8.3-1.6.5-2.5.2-.9.6-1.8.8-2.8.3-1 .8-1.9 1.2-3 .5-1 1.1-2 1.7-3.1.7-1 1.4-2.1 2.2-3.1 1.6-2.1 3.7-3.9 6-5.6 2.3-1.7 5-3 7.9-4.1.7-.2 1.5-.4 2.2-.7.7-.3 1.5-.3 2.3-.5.8-.2 1.5-.3 2.3-.4l1.2-.1.6-.1h.6c1.5 0 2.9-.1 4.5.2.8.1 1.6.1 2.4.3.8.2 1.5.3 2.3.5 3 .8 5.9 2 8.5 3.6 2.6 1.6 4.9 3.4 6.8 5.4 1 1 1.8 2.1 2.7 3.1.8 1.1 1.5 2.1 2.1 3.2.6 1.1 1.2 2.1 1.6 3.1.4 1 .9 2 1.2 3 .3 1 .6 1.9.8 2.7.2.9.3 1.6.5 2.4.1.4.1.7.2 1 0 .3.1.6.1.9.1.6.1 1 .1 1.4.4 1 .4 1.4.4 1.4.2 2.2-1.5 4.1-3.7 4.3s-4.1-1.5-4.3-3.7v-.7-.9-1.1-.7c0-.2-.1-.5-.1-.8-.1-.6-.1-1.2-.2-1.9s-.3-1.4-.4-2.2c-.2-.8-.5-1.6-.7-2.4-.3-.8-.7-1.7-1.1-2.6-.5-.9-.9-1.8-1.5-2.7-.6-.9-1.2-1.8-1.9-2.7-1.4-1.8-3.2-3.4-5.2-4.9-2-1.5-4.4-2.7-6.9-3.6-.6-.2-1.3-.4-1.9-.6-.7-.2-1.3-.3-1.9-.4-1.2-.3-2.8-.4-4.2-.5h-2c-.7 0-1.4.1-2.1.1-.7.1-1.4.1-2 .3-.7.1-1.3.3-2 .4-2.6.7-5.2 1.7-7.5 3.1-2.2 1.4-4.3 2.9-6 4.7-.9.8-1.6 1.8-2.4 2.7-.7.9-1.3 1.9-1.9 2.8-.5 1-1 1.9-1.4 2.8-.4.9-.8 1.8-1 2.6-.3.9-.5 1.6-.7 2.4-.2.7-.3 1.4-.4 2.1-.1.3-.1.6-.2.9 0 .3-.1.6-.1.8 0 .5-.1.9-.1 1.3-.2.7-.2 1.1-.2 1.1z"
            >
              <animateTransform
                attributeType="xml"
                attributeName="transform"
                type="rotate"
                from="0 40 40"
                to="360 40 40"
                dur="0.6s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        )}
        {errorMessage && formatText(errorMessage)}
      </Container>
      <Footer>
        {isValidationCodeSent ? (
          <Button color="blue" primary fluid disabled={isButtonDisabled} onClick={startValidation}>
            Skicka valideringskod
          </Button>
        ) : (
          <Button color="blue" primary fluid disabled={isButtonDisabled} onClick={sendSmsCode}>
            Skicka sms med valideringskod
          </Button>
        )}
      </Footer>
    </div>
  );
};

export default SmsChallenge;
